import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Sparkles, ArrowRight, AlertCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import WeKitLogo from "@/components/WeKitLogo";

type PublicReport = {
  owner_name: string;
  age_group: string;
  scored_at: string;
  trait_scores: Record<string, { score: number; count?: number } | number>;
  narrative: {
    headline: string;
    archetype_story: string;
    superpowers_story: string;
    career_rationale: string;
    growth_edges: { trait: string; why: string; practice: string }[];
    thirty_day_plan: { week: number; focus: string; actions: string[] }[];
    closing_note: string;
  } | null;
  generated_at: string | null;
};

export default function SharedReport() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<PublicReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/public-report?token=${encodeURIComponent(token)}`;
    fetch(url, {
      headers: { apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY },
    })
      .then(async (r) => {
        const body = await r.json();
        if (!r.ok) throw new Error(body?.error || "Failed to load");
        setData(body);
      })
      .catch((e) => setError(e.message ?? "Error"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Card className="max-w-md border-border/50">
          <CardContent className="p-8 text-center">
            <AlertCircle className="mx-auto h-10 w-10 text-destructive" />
            <h2 className="mt-4 font-display text-xl font-bold text-foreground">
              Link unavailable
            </h2>
            <p className="mt-2 text-muted-foreground text-sm">
              {error ?? "This share link doesn't exist or has been revoked."}
            </p>
            <Button asChild className="mt-6 rounded-full">
              <Link to="/">Discover your own Career DNA</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const topTraits = Object.entries(data.trait_scores ?? {})
    .map(([t, v]) => [t, typeof v === "number" ? v : v.score] as [string, number])
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="container mx-auto flex h-16 items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2">
            <WeKitLogo size="sm" />
            <span className="font-display text-lg font-bold text-foreground">WeKIT™</span>
          </Link>
          <Button size="sm" asChild className="rounded-full hover-glow">
            <Link to="/">Take the assessment <ArrowRight className="ml-1 h-3 w-3" /></Link>
          </Button>
        </div>
      </header>

      <main className="container mx-auto max-w-3xl px-4 sm:px-6 py-10 space-y-8">
        <section className="text-center">
          <Badge className="rounded-full bg-primary/10 text-primary border-primary/20 px-4 py-1.5">
            <Sparkles className="mr-1.5 h-3.5 w-3.5" /> Shared WeKIT™ Career Clarity 360
          </Badge>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-foreground">
            {data.owner_name}'s Career DNA
          </h1>
          {data.narrative?.headline && (
            <p className="mt-4 font-display text-xl sm:text-2xl gradient-text leading-tight">
              {data.narrative.headline}
            </p>
          )}
        </section>

        {/* Top traits */}
        <Card className="border-border/50">
          <CardContent className="p-6">
            <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4">
              Top traits
            </h3>
            <div className="space-y-3">
              {topTraits.map(([t, s]) => (
                <div key={t}>
                  <div className="flex items-center justify-between mb-1 text-sm">
                    <span className="font-medium text-foreground">{t}</span>
                    <span className="font-mono text-primary">{Math.round(s)}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-primary to-accent"
                      style={{ width: `${Math.min(Math.round(s), 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {data.narrative ? (
          <>
            <Section title="Who they are">
              <Prose text={data.narrative.archetype_story} />
            </Section>
            <Section title="Their superpowers">
              <Prose text={data.narrative.superpowers_story} />
            </Section>
            <Section title="Why these careers fit">
              <Prose text={data.narrative.career_rationale} />
            </Section>
            <Section title="Growth edges">
              <div className="grid gap-3 sm:grid-cols-2">
                {data.narrative.growth_edges.map((g, i) => (
                  <div key={i} className="rounded-xl border border-border/50 bg-card/50 p-4">
                    <Badge variant="outline" className="text-xs">{g.trait}</Badge>
                    <p className="mt-2 text-sm text-foreground">{g.why}</p>
                    <p className="mt-3 text-xs text-primary font-medium">Try this →</p>
                    <p className="text-sm text-muted-foreground">{g.practice}</p>
                  </div>
                ))}
              </div>
            </Section>
            <Section title="Their 30-day plan">
              <div className="space-y-3">
                {data.narrative.thirty_day_plan.map((w) => (
                  <div key={w.week} className="rounded-xl border border-border/50 bg-card/50 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-xs font-display font-bold text-primary">
                        W{w.week}
                      </span>
                      <h4 className="font-display font-semibold text-foreground">{w.focus}</h4>
                    </div>
                    <ul className="space-y-1.5 pl-9 text-sm text-muted-foreground list-disc">
                      {w.actions.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </Section>
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-5">
              <p className="text-sm text-foreground italic leading-relaxed">"{data.narrative.closing_note}"</p>
              <p className="mt-2 text-xs text-muted-foreground">— YOMA, AI mentor</p>
            </div>
          </>
        ) : (
          <p className="text-sm text-muted-foreground text-center">
            This explorer hasn't generated their narrative yet.
          </p>
        )}

        <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-accent/5">
          <CardContent className="p-8 text-center">
            <h2 className="font-display text-2xl font-bold text-foreground">
              Curious about your own Career DNA?
            </h2>
            <p className="mt-2 text-muted-foreground text-sm">
              Take WeKIT™ Career Clarity 360 — 26 traits, 5,000+ careers of the future, 1 personalized AI report.
            </p>
            <Button asChild size="lg" className="mt-5 rounded-full hover-glow">
              <Link to="/">Discover Yours <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
        {title}
      </h3>
      {children}
    </div>
  );
}

function Prose({ text }: { text: string }) {
  return (
    <div className="space-y-3 text-[15px] leading-relaxed text-foreground">
      {text.split(/\n\n+/).map((p, i) => <p key={i}>{p}</p>)}
    </div>
  );
}
